import queryClient from 'api/queryClient'
import api from 'api/api'
import { GetQuestsResponse } from 'api/api'
import { getQuestsQueryKey } from './useGetQuests'
import { buildStreamQuestRowRef, stripStreamQuestRowContext } from './streamQuestRows'
import { cacheQuest } from 'state/cache'
import { withDepth } from 'components/filters/useStreamFilters'
import useStore from 'state/knovStore'
import { canCommitNotificationPrefetch } from './notificationPrefetchPolicy'
import { getSpaceNotificationBadgeEventVersion } from './notificationBadgeRefetchPolicy'

// Warm the notifications stream query cache so UI can show PTR instead of blank
export async function prefetchNotifications() {
    try {
        if (useStore.getState().numNoti > 0) {
            return
        }

        const activeSpaceIdAtStart = useStore.getState().activeSpaceId
        const badgeEventVersionAtStart = getSpaceNotificationBadgeEventVersion(activeSpaceIdAtStart)
        const filter = withDepth({ notifications: true })
        const queryKey = getQuestsQueryKey(filter, { clear: true })
        const dataUpdatedAtAtStart = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0

        const data = await api.getQuests({ ...filter, page: 1 })

        // This request intentionally does not clear notifications, but it warms the cache used
        // by the clear=true notification panel. If that panel fetched newer data while this
        // slower request was in flight, committing here would replace the fresh rows with the
        // older prefetch response. A badge appearing mid-flight is the same stale-result signal.
        const queryStateNow = queryClient.getQueryState(queryKey)
        const activeSpaceIdNow = useStore.getState().activeSpaceId
        if (
            !canCommitNotificationPrefetch({
                unreadCount: useStore.getState().numNoti,
                dataUpdatedAtAtStart,
                dataUpdatedAtNow: queryStateNow?.dataUpdatedAt ?? 0,
                badgeEventVersionAtStart,
                badgeEventVersionNow: getSpaceNotificationBadgeEventVersion(activeSpaceIdAtStart),
                spaceIdAtStart: activeSpaceIdAtStart,
                spaceIdNow: activeSpaceIdNow,
                isInvalidatedNow: queryStateNow?.isInvalidated,
                isFetchingNow: queryStateNow?.fetchStatus === 'fetching',
            })
        ) {
            return
        }

        if (data?.quests) {
            data.quests.forEach(quest => cacheQuest(stripStreamQuestRowContext(quest)))
        }

        const typedData = data as GetQuestsResponse
        const normalizedPinnedQuest = typedData.pinned_quest || typedData.pinnedQuest || null
        if (normalizedPinnedQuest) cacheQuest(stripStreamQuestRowContext(normalizedPinnedQuest))

        // Seed the infinite query cache with the first page
        const page = {
            pinnedQuest: normalizedPinnedQuest,
            page: 1,
            quests: (data?.quests || []).map(buildStreamQuestRowRef),
        }

        queryClient.setQueryData(queryKey, {
            pageParams: [1],
            pages: [page],
        })
    } catch (e) {
        // Best-effort prefetch; ignore errors
        console.warn('prefetchNotifications failed', e)
    }
}
